import Loading from '@/shared/components/Carregando';
import imprimirDiv from '@/shared/utils/impressao';
import { DataAmigavel } from '@/shared/utils/datas';
import { Fragment, useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import {
  GetRelatorioEventosInstitucionais,
  RelatorioEventoInstitucionalItemDto,
} from '@/modules/business/evento-institucional/evento-institucional.service';
import { TipoPeriodoDescansoList } from '@/shared/constants/enumlist';
import { toast } from 'react-toastify';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';

function rotuloTipo(tipo: number): string {
  return TipoPeriodoDescansoList.find((item) => item.value === tipo)?.name ?? `Tipo ${tipo}`;
}

function abertosIniciais(itens: RelatorioEventoInstitucionalItemDto[]): Set<number> {
  return new Set(
    itens.filter((evento) => (evento.ausencias?.length || 0) > 0).map((evento) => evento.id),
  );
}

export default function RelatorioEventosInstitucionaisPage() {
  const { setActions } = useBreadcrumbActions();
  const [loading, setLoading] = useState(true);
  const [itens, setItens] = useState<RelatorioEventoInstitucionalItemDto[]>([]);
  const [abertos, setAbertos] = useState<Set<number>>(new Set());

  useEffect(() => {
    GetRelatorioEventosInstitucionais()
      .then((result) => {
        const lista = Array.isArray(result) ? result : [];
        setItens(lista);
        setAbertos(abertosIniciais(lista));
      })
      .catch((res) => {
        toast.error(String(res) || 'Falha ao carregar o relatório.');
        setItens([]);
        setAbertos(new Set());
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setActions({
      print: {
        label: 'Imprimir',
        onClick: () => {
          setAbertos(new Set(itens.map((evento) => evento.id)));
          window.setTimeout(() => {
            imprimirDiv('conteudo-relatorio-eventos-institucionais');
          }, 100);
        },
      },
    });
    return () => setActions({});
  }, [setActions, itens]);

  const alternar = (id: number) => {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="row">
      <div className="col-12">
        <h3>Relatório de eventos institucionais</h3>
        <span className="blockquote-footer pb-3 text-end">
          Eventos e ausências vinculadas.
        </span>
        <hr />
      </div>

      <div className="col-12" id="conteudo-relatorio-eventos-institucionais">
        <h4 className="text-center mb-4">EVENTOS INSTITUCIONAIS</h4>

        {itens.length === 0 ? (
          <p className="text-muted">Nenhum evento institucional cadastrado.</p>
        ) : (
          itens.map((evento) => {
            const aberto = abertos.has(evento.id);
            const collapseId = `evento-institucional-${evento.id}`;
            const qtdAusencias = evento.ausencias?.length || 0;

            return (
              <Fragment key={evento.id}>
                <div className="card mb-3">
                  <div
                    className="card-header d-flex justify-content-between align-items-center"
                    role="button"
                    tabIndex={0}
                    onClick={() => alternar(evento.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        alternar(evento.id);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    aria-expanded={aberto}
                    aria-controls={collapseId}
                  >
                    <span>
                      <span className="me-2" aria-hidden>
                        {aberto ? '▼' : '▶'}
                      </span>
                      <strong>{evento.titulo}</strong>
                      <span className="ms-2 text-muted">
                        {DataAmigavel(evento.data_inicial)} até {DataAmigavel(evento.data_final)}
                      </span>
                    </span>
                    <span className="badge bg-secondary">{qtdAusencias}</span>
                  </div>
                  <Collapse in={aberto}>
                    <div id={collapseId}>
                      <div className="card-body">
                        {evento.descricao ? <p className="mb-2">{evento.descricao}</p> : null}
                        <h6 className="mb-2">Ausências vinculadas ({qtdAusencias})</h6>
                        {qtdAusencias === 0 ? (
                          <p className="text-muted mb-0">Nenhuma ausência vinculada.</p>
                        ) : (
                          <ul>
                            {evento.ausencias.map((ausencia) => (
                              <li key={ausencia.periodoId}>
                                {ausencia.defensorNome} — {rotuloTipo(ausencia.tipo)} (
                                {DataAmigavel(ausencia.data_inicial)} a{' '}
                                {DataAmigavel(ausencia.data_final)})
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </Collapse>
                </div>
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}
